import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, Trash2, Download, FileText, Search } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export default function Archive() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get('/documents' + (q ? `?q=${encodeURIComponent(q)}` : '')); setDocs(data); }
    catch { setDocs([]); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, navigate, q]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        const base64 = String(dataUrl).split(',')[1];
        await api.post('/documents', { filename: file.name, mime_type: file.type || 'application/octet-stream', data_base64: base64, tags: [], notes: '' });
        toast.success('OK');
        load();
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error(t.common.error);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const del = async (id) => {
    try { await api.delete(`/documents/${id}`); setDocs((d) => d.filter(x => x.id !== id)); }
    catch { toast.error(t.common.error); }
  };

  const download = async (d) => {
    try {
      const { data } = await api.get(`/documents/${d.id}/download`);
      const blob = await (await fetch(`data:${data.mime_type};base64,${data.data_base64}`)).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = data.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t.common.error); }
  };

  if (loading || !user) return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">{t.common.loading}</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-tight">{t.archive.title}</h1>
      <p className="mt-4 text-lg text-muted-foreground">{t.archive.subtitle}</p>

      <div className="mt-10 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.archive.search}
            data-testid="archive-search-input"
            className="w-full pl-11 pr-4 py-3 rounded-full bg-secondary border border-transparent focus:border-primary focus:bg-background outline-none text-sm" />
        </div>
        <label className="cursor-pointer inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity" data-testid="archive-upload-label">
          <Upload size={16} />
          {uploading ? t.common.loading : t.archive.upload}
          <input ref={fileInput} type="file" className="hidden" onChange={onFile} data-testid="archive-file-input" />
        </label>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{t.archive.max_size}</p>

      <div className="mt-12" data-testid="archive-list">
        {busy ? <p className="text-muted-foreground">{t.common.loading}</p> : docs.length === 0 ? (
          <p className="text-muted-foreground text-center py-16">{t.archive.empty}</p>
        ) : (
          <div className="rounded-3xl border border-border overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-widest">{t.archive.filename}</th>
                  <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-widest hidden sm:table-cell">{t.archive.size}</th>
                  <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-widest hidden md:table-cell">{t.archive.date}</th>
                  <th className="text-right py-3 px-4 font-medium text-xs uppercase tracking-widest"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-t border-border" data-testid={`archive-doc-${d.id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <FileText size={16} className="text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{d.filename}</div>
                          <div className="text-xs text-muted-foreground">{d.mime_type}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground hidden sm:table-cell">{fmtSize(d.size)}</td>
                    <td className="py-3 px-4 text-muted-foreground hidden md:table-cell">{new Date(d.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-right space-x-2">
                      <button onClick={() => download(d)} data-testid={`archive-download-${d.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-secondary transition-colors" aria-label="Download">
                        <Download size={14} />
                      </button>
                      <button onClick={() => del(d.id)} data-testid={`archive-delete-${d.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors" aria-label="Delete">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
